import React, { useState, useRef, useCallback } from 'react';
import { utils } from '../../utils';
import { importarEtiquetas } from '../../services/etiquetas';
import { Cliente } from '../../services/clientes';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  clientes: Cliente[];
}

interface ArquivoFila {
  file: File;
  clienteId: string;
  clienteNome: string;
  status: 'pendente' | 'importando' | 'sucesso' | 'erro';
  resultado?: { total: number; importadas: number; erros: number };
  erro?: string;
}

function extrairCnpjDoXml(conteudo: string): string | null {
  // Buscar CNPJ dentro da tag <emit>
  const emitMatch = conteudo.match(/<emit>([\s\S]*?)<\/emit>/i);
  if (!emitMatch) return null;
  const cnpjMatch = emitMatch[1].match(/<CNPJ>(\d+)<\/CNPJ>/i);
  return cnpjMatch ? cnpjMatch[1] : null;
}

function encontrarClientePorCnpj(cnpj: string, clientes: Cliente[]): Cliente | undefined {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return clientes.find(c => c.cnpj.replace(/\D/g, '') === cnpjLimpo);
}

const ImportarEtiquetasModal: React.FC<Props> = ({ onClose, onSuccess, clientes }) => {
  const [clienteId, setClienteId] = useState('');
  const [arquivos, setArquivos] = useState<ArquivoFila[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [autoDetectado, setAutoDetectado] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processarArquivosSelecionados = useCallback(async (files: FileList | File[]) => {
    const extensoesValidas = ['xml', 'edi', 'txt', 'dat'];
    const novosArquivos: ArquivoFila[] = [];
    let clienteAutoDetectado: Cliente | undefined;
    let primeiroXml = true;

    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!extensoesValidas.includes(ext || '')) {
        utils.showNotification(`Arquivo "${file.name}" ignorado - tipo não suportado`, { type: 'warning' });
        continue;
      }

      let clienteArquivo = clienteId;
      let clienteNome = clientes.find(c => c.id.toString() === clienteId)?.razao_social || '';

      // Auto-detectar cliente por CNPJ para arquivos XML
      if (ext === 'xml') {
        try {
          const conteudo = await file.text();
          const cnpj = extrairCnpjDoXml(conteudo);
          if (cnpj) {
            const cliente = encontrarClientePorCnpj(cnpj, clientes);
            if (cliente) {
              clienteArquivo = cliente.id.toString();
              clienteNome = cliente.razao_social;
              if (primeiroXml) {
                clienteAutoDetectado = cliente;
                primeiroXml = false;
              }
            }
          }
        } catch {
          // Falha na leitura - seguir sem auto-detecção
        }
      }

      novosArquivos.push({
        file,
        clienteId: clienteArquivo,
        clienteNome,
        status: 'pendente'
      });
    }

    if (novosArquivos.length === 0) return;

    // Auto-selecionar cliente no select se detectado e nenhum selecionado
    if (clienteAutoDetectado && !clienteId) {
      setClienteId(clienteAutoDetectado.id.toString());
      setAutoDetectado(true);
    }

    setArquivos(prev => [...prev, ...novosArquivos]);
  }, [clienteId, clientes]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processarArquivosSelecionados(files);
    }
    // Limpar input para poder selecionar os mesmos arquivos novamente
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removerArquivo = (index: number) => {
    setArquivos(prev => prev.filter((_, i) => i !== index));
  };

  const handleImportar = async () => {
    // Para arquivos sem cliente auto-detectado, usar o cliente selecionado no select
    const arquivosParaImportar = arquivos.map(a => ({
      ...a,
      clienteId: a.clienteId || clienteId
    }));

    const semCliente = arquivosParaImportar.some(a => !a.clienteId);
    if (semCliente) {
      utils.showNotification('Selecione um cliente para os arquivos sem detecção automática', { type: 'error' });
      return;
    }

    if (arquivosParaImportar.length === 0) {
      utils.showNotification('Selecione pelo menos um arquivo', { type: 'error' });
      return;
    }

    setIsUploading(true);
    let houveImportacao = false;

    for (let i = 0; i < arquivosParaImportar.length; i++) {
      const arq = arquivosParaImportar[i];
      if (arq.status === 'sucesso') continue; // Pular já importados

      setArquivos(prev => prev.map((a, idx) =>
        idx === i ? { ...a, clienteId: arq.clienteId, status: 'importando' } : a
      ));

      try {
        const result = await importarEtiquetas(parseInt(arq.clienteId), arq.file);
        setArquivos(prev => prev.map((a, idx) =>
          idx === i ? { ...a, status: 'sucesso', resultado: result.dados } : a
        ));
        if (result.dados.importadas > 0) houveImportacao = true;
      } catch (error: any) {
        setArquivos(prev => prev.map((a, idx) =>
          idx === i ? { ...a, status: 'erro', erro: error.message || 'Erro ao importar' } : a
        ));
      }
    }

    setIsUploading(false);

    if (houveImportacao) {
      utils.showNotification('Importação concluída com sucesso', { type: 'success' });
      setTimeout(() => onSuccess(), 1500);
    }
  };

  const totalArquivos = arquivos.length;
  const totalSucesso = arquivos.filter(a => a.status === 'sucesso').length;
  const totalErro = arquivos.filter(a => a.status === 'erro').length;
  const todosProcessados = totalArquivos > 0 && (totalSucesso + totalErro) === totalArquivos;
  const temPendentes = arquivos.some(a => a.status === 'pendente' || a.status === 'erro');

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }} onClick={(e) => { if (e.target === e.currentTarget && !isUploading) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '24px',
        width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>
            <i className="fas fa-file-import" style={{ marginRight: '8px', color: '#3b82f6' }}></i>
            Importar Pedidos
          </h2>
          <button onClick={onClose} disabled={isUploading} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Seleção de Cliente */}
        <div style={{ marginBottom: '16px' }}>
          <label className="form-label">
            Cliente {arquivos.length === 0 ? '*' : '(padrão para arquivos sem detecção)'}
          </label>
          <select
            className="form-control"
            value={clienteId}
            onChange={(e) => { setClienteId(e.target.value); setAutoDetectado(false); }}
            disabled={isUploading}
          >
            <option value="">Selecione o cliente...</option>
            {clientes.map(c => (
              <option key={c.id} value={c.id}>{c.razao_social}</option>
            ))}
          </select>
          {autoDetectado && (
            <p style={{ fontSize: '12px', color: '#16a34a', margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <i className="fas fa-magic"></i>
              Cliente detectado automaticamente pelo CNPJ do XML
            </p>
          )}
        </div>

        {/* Upload de Arquivos */}
        <div style={{ marginBottom: '20px' }}>
          <label className="form-label">Arquivos (XML ou EDI) *</label>
          <div
            onClick={() => !isUploading && fileInputRef.current?.click()}
            style={{
              border: '2px dashed #d1d5db',
              borderRadius: '10px',
              padding: arquivos.length > 0 ? '16px' : '30px 20px',
              textAlign: 'center',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              backgroundColor: '#fafafa'
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isUploading && e.dataTransfer.files.length > 0) {
                processarArquivosSelecionados(e.dataTransfer.files);
              }
            }}
          >
            <i className="fas fa-cloud-upload-alt" style={{ fontSize: arquivos.length > 0 ? '20px' : '36px', color: '#9ca3af', marginBottom: '6px', display: 'block' }}></i>
            <p style={{ fontWeight: 500, color: '#374151', margin: '0 0 4px', fontSize: arquivos.length > 0 ? '13px' : '14px' }}>
              Clique ou arraste arquivos aqui
            </p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
              XML (NF-e), EDI, TXT, DAT - Suporta m&uacute;ltiplos arquivos
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,.edi,.txt,.dat"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>

        {/* Lista de Arquivos na Fila */}
        {arquivos.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ margin: 0 }}>
                {totalArquivos} arquivo{totalArquivos > 1 ? 's' : ''} na fila
              </label>
              {todosProcessados && (
                <span style={{ fontSize: '12px', color: totalErro > 0 ? '#ef4444' : '#16a34a', fontWeight: 600 }}>
                  {totalSucesso} importado{totalSucesso > 1 ? 's' : ''}{totalErro > 0 ? `, ${totalErro} com erro` : ''}
                </span>
              )}
            </div>
            <div style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              {arquivos.map((arq, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px',
                  borderBottom: idx < arquivos.length - 1 ? '1px solid #f3f4f6' : 'none',
                  backgroundColor: arq.status === 'sucesso' ? '#f0fdf4' : arq.status === 'erro' ? '#fef2f2' : arq.status === 'importando' ? '#eff6ff' : '#fff'
                }}>
                  {/* Ícone de status */}
                  <div style={{ flexShrink: 0, width: '20px', textAlign: 'center' }}>
                    {arq.status === 'pendente' && <i className="fas fa-file-code" style={{ color: '#6b7280' }}></i>}
                    {arq.status === 'importando' && <div className="loading-spinner-small"></div>}
                    {arq.status === 'sucesso' && <i className="fas fa-check-circle" style={{ color: '#16a34a' }}></i>}
                    {arq.status === 'erro' && <i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i>}
                  </div>

                  {/* Info do arquivo */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {arq.file.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      {arq.clienteNome ? (
                        <span><i className="fas fa-building" style={{ marginRight: '3px' }}></i>{arq.clienteNome}</span>
                      ) : (
                        <span style={{ color: '#f59e0b' }}><i className="fas fa-exclamation-triangle" style={{ marginRight: '3px' }}></i>Cliente não detectado</span>
                      )}
                      {arq.resultado && (
                        <span style={{ marginLeft: '8px' }}>
                          {arq.resultado.importadas}/{arq.resultado.total} importadas
                        </span>
                      )}
                      {arq.erro && (
                        <span style={{ marginLeft: '8px', color: '#ef4444' }}>{arq.erro}</span>
                      )}
                    </div>
                  </div>

                  {/* Tamanho */}
                  <div style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>
                    {(arq.file.size / 1024).toFixed(0)} KB
                  </div>

                  {/* Botão remover */}
                  {arq.status === 'pendente' && !isUploading && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removerArquivo(idx); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px', flexShrink: 0 }}
                    >
                      <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Barra de progresso durante importação */}
        {isUploading && totalArquivos > 1 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ height: '6px', borderRadius: '3px', backgroundColor: '#e5e7eb', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '3px',
                backgroundColor: '#04C64F',
                width: `${((totalSucesso + totalErro) / totalArquivos) * 100}%`,
                transition: 'width 0.3s'
              }}></div>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', margin: '6px 0 0' }}>
              Processando {totalSucesso + totalErro + 1} de {totalArquivos}...
            </p>
          </div>
        )}

        {/* Botões */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={onClose} disabled={isUploading}>
            {todosProcessados ? 'Fechar' : 'Cancelar'}
          </button>
          {temPendentes && (
            <button
              className="btn btn-primary"
              onClick={handleImportar}
              disabled={isUploading || arquivos.length === 0}
            >
              {isUploading ? (
                <><div className="loading-spinner-small" style={{ marginRight: '8px' }}></div> Importando...</>
              ) : (
                <><i className="fas fa-file-import" style={{ marginRight: '6px' }}></i> Importar {totalArquivos > 1 ? `${totalArquivos} arquivos` : ''}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportarEtiquetasModal;
